import { numberHelpers } from '@approvalmax/utils';
import uniq from 'lodash/uniq';
import { domain } from 'modules/data';
import { routingService } from 'services/routing';
import { GetNetSuiteBillPaymentBills } from 'shared/data/webApp/v2';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteBillPaymentFieldSettings } from 'shared/hooks';
import { NetSuiteApplyListLineExpanded, NetSuiteBillPaymentFields } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { CustomFieldsState, NetSuiteCreditListLineExpanded } from '../data/types';
import { validateCustomFields } from '../helpers';
import { isFieldMandatory, isMandatoryFieldEmpty } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { messages } from './NetSuiteBillPaymentRequestForm.messages';

export const mapBillPaymentBillAttachment = (
    attachment: GetNetSuiteBillPaymentBills['response']['items'][number]['attachments'][number]
) => ({
    ...attachment,
    id: attachment.attachmentId,
    name: attachment.name ?? attachment.id,
});

export const getBillPaymentValidationErrors = (data: {
    fields: Partial<NetSuiteBillPaymentFields>;
    customFieldsValues: CustomFieldsState;
    customFieldsList: domain.Field[];
    workflowId: string;
    billPaymentFieldSettings: ReturnType<typeof useNetSuiteBillPaymentFieldSettings>;
    companyCurrency: string;
    requestCurrencyISOCode: string | null;
    defaultCurrencyRate: number | undefined;
    applyList: NetSuiteApplyListLineExpanded[];
    creditList: NetSuiteCreditListLineExpanded[];
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments: domain.RequestAttachment[];
}): string[] => {
    const {
        fields,
        customFieldsValues,
        customFieldsList,
        workflowId,
        companyCurrency,
        requestCurrencyISOCode,
        billPaymentFieldSettings,
        defaultCurrencyRate,
        applyList,
        creditList,
        attachmentsFieldSettings,
        attachments,
    } = data;

    const errors: string[] = [];

    if (!fields.payee || !fields.transactionDate || !fields.account || !fields.payablesAccount) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isFieldMandatory(attachmentsFieldSettings.attachments) && attachments.length === 0) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.payeeAddress, billPaymentFieldSettings.payeeAddress)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.department, billPaymentFieldSettings.department)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.class, billPaymentFieldSettings.class)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.location, billPaymentFieldSettings.location)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.memo, billPaymentFieldSettings.memo)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (
        requestCurrencyISOCode !== companyCurrency &&
        !numberHelpers.isNumber(fields.exchangeRate) &&
        !numberHelpers.isNumber(defaultCurrencyRate)
    ) {
        errors.push(messages.exchangeRateError);
    }

    if (!applyList.length && !creditList.length) {
        errors.push(messages.emptyLinesError);
    }

    if (applyList.some((item) => !numberHelpers.isNumber(item.paymentAmount))) {
        errors.push(messages.mandatoryFieldsError);
    }

    validateCustomFields({
        customFieldsValues: Object.values(customFieldsValues),
        customFieldsLevels: ['Header', 'Header And Lines'],
        customFieldsList,
        workflowId,
        errors,
    });

    return uniq(errors);
};

export const redirectToRequest = (request: domain.Request) =>
    routingService.push(getPath(Path.request, request.id, request.companyId));
